/**
 * Country-tab manual selection — horizontal tabs switching senders/texts pickers per country.
 * Each country has independent selections.
 */

import { useCallback, useState } from 'react';
import { Hand } from 'lucide-react';

import { cn } from '@/lib/utils';
import { countryToFlagEmoji } from '@/utils/country-flag-emoji';
import { SendersPickerSection, TextsPickerSection } from '@/features/smswiser-campaigns/components/drop-modals';
import { ModalSection } from '@/features/smswiser-campaigns/components/ScaleDropModal/shared';

import type { useMultiCountryBulkEditForm } from '../hooks';
import type { useMultiCountrySentScheduledEditForm } from '@/features/smswiser-campaigns/components/MultiCountrySentScheduledEdit/hooks/useMultiCountrySentScheduledEditForm';

type Form =
  | ReturnType<typeof useMultiCountryBulkEditForm>
  | ReturnType<typeof useMultiCountrySentScheduledEditForm>;

interface Props {
  form: Form;
  countryKeys: string[];
  sendersList: string[];
  textsList: string[];
  isBusy: boolean;
}

export function CountryManualPickers({ form, countryKeys, sendersList, textsList, isBusy }: Props) {
  const [activeCountry, setActiveCountry] = useState(countryKeys[0] ?? '');
  const countrySenders = form.perCountrySenders[activeCountry] ?? [];
  const countryTexts = form.perCountryTexts[activeCountry] ?? [];

  const handleToggleSender = useCallback(
    (name: string) => form.toggleCountrySender(activeCountry, name),
    [form, activeCountry],
  );
  const handleToggleText = useCallback(
    (text: string) => form.toggleCountryText(activeCountry, text),
    [form, activeCountry],
  );
  const handleSelectAllSenders = useCallback(
    () => form.setCountrySenders(activeCountry, countrySenders.length === sendersList.length ? [] : [...sendersList]),
    [form, activeCountry, countrySenders.length, sendersList],
  );
  const handleSelectAllTexts = useCallback(
    () => form.setCountryTexts(activeCountry, countryTexts.length === textsList.length ? [] : [...textsList]),
    [form, activeCountry, countryTexts.length, textsList],
  );
  const handleClearSenders = useCallback(
    () => form.setCountrySenders(activeCountry, []),
    [form, activeCountry],
  );
  const handleClearTexts = useCallback(
    () => form.setCountryTexts(activeCountry, []),
    [form, activeCountry],
  );

  if (countryKeys.length === 0) return null;

  return (
    <ModalSection
      title="Manual Selection"
      icon={<Hand className="h-3.5 w-3.5" />}
      badge={<span className="ml-1 text-[10px] text-muted-foreground">Per-country senders & texts</span>}
    >
      <div className="space-y-3">
        <div className="flex gap-1 rounded-lg border border-border/60 bg-muted/30 p-1">
          {countryKeys.map((ck) => {
            const hasData = (form.perCountryTexts[ck]?.length ?? 0) > 0 || (form.perCountrySenders[ck]?.length ?? 0) > 0;
            return (
              <button
                key={ck} type="button" onClick={() => setActiveCountry(ck)}
                className={cn(
                  'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                  activeCountry === ck
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground',
                  hasData && activeCountry !== ck && 'text-primary',
                )}
              >
                <span>{countryToFlagEmoji(ck)}</span> {ck}
              </button>
            );
          })}
        </div>

        <div className="grid gap-3 lg:grid-cols-2">
          <SendersPickerSection
            sendersList={sendersList}
            selectedSenders={countrySenders}
            onToggleSender={handleToggleSender}
            onSelectAll={handleSelectAllSenders}
            onClearSelection={handleClearSenders}
            search="" onSearch={() => {}}
            newValue="" setNewValue={() => {}}
            isBusy={isBusy} expanded onToggleExpand={() => {}}
          />
          <TextsPickerSection
            textsList={textsList}
            selectedTexts={countryTexts}
            onToggleText={handleToggleText}
            onSelectAll={handleSelectAllTexts}
            onClearSelection={handleClearTexts}
            search="" onSearch={() => {}}
            newValue="" setNewValue={() => {}}
            isBusy={isBusy} expanded onToggleExpand={() => {}}
          />
        </div>
      </div>
    </ModalSection>
  );
}
